/**
 * HTTP клиент для работы в веб и нативных приложениях
 * Использует нативный HTTP плагин для Android/iOS для обхода CORS
 * Поддерживает Supabase Auth (JWT токены)
 */

import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { authHelpers } from '../lib/supabase.js';
import { formatErrorMessage } from './formatErrorMessage.js';

// Базовый URL API
const BASE_API_URL = 'https://aimarkethub.ru/api';

/**
 * Проверяет, работаем ли мы в нативном приложении
 */
const isNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Выполняет HTTP запрос
 * В нативном приложении использует fetch с полным URL
 * В вебе использует axios с относительным путём
 * Автоматически добавляет JWT токен от Supabase, если доступен
 */
export async function httpRequest(method, endpoint, data = null, options = {}) {
  const url = isNativePlatform() 
    ? `${BASE_API_URL}${endpoint}` 
    : `/api${endpoint}`;
  
  console.log(`[HTTP] ${method} ${url}`, { isNative: isNativePlatform() });
  
  // Получаем JWT токен от Supabase (если доступен)
  let accessToken = null;
  try {
    accessToken = await authHelpers.getAccessToken();
    if (accessToken) {
      console.log('[HTTP] JWT токен получен, длина:', accessToken.length);
    } else {
      console.debug('[HTTP] Supabase токен недоступен, используем старую авторизацию');
    }
  } catch (error) {
    // Игнорируем ошибки получения токена (может быть не настроен Supabase)
    console.debug('[HTTP] Ошибка получения Supabase токена:', error.message);
  }
  
  try {
    if (isNativePlatform()) {
      // Для нативного приложения используем fetch с полным URL
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      };
      
      // Добавляем JWT токен от Supabase, если доступен
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      } else {
        // Fallback на старую систему (x-session-token)
        const sessionToken = localStorage.getItem('session_token');
        if (sessionToken) {
          headers['x-session-token'] = sessionToken;
        }
      }
      
      const fetchOptions = {
        method: method.toUpperCase(),
        headers,
      };
      
      if (data && (method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT')) {
        fetchOptions.body = JSON.stringify(data);
      }
      
      console.log('[HTTP Native] Отправка запроса:', url, fetchOptions);
      
      const response = await fetch(url, fetchOptions);
      const responseData = await response.json();
      
      console.log('[HTTP Native] Ответ:', response.status, responseData);
      
      return {
        data: responseData,
        status: response.status,
        ok: response.ok,
      };
    } else {
      // Для веба используем axios
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };
      
      // Добавляем JWT токен от Supabase, если доступен
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      } else {
        // Fallback на старую систему (x-session-token)
        const sessionToken = localStorage.getItem('session_token');
        if (sessionToken) {
          headers['x-session-token'] = sessionToken;
        }
      }
      
      // Динамический таймаут: больше для больших файлов
      // Базовый таймаут 10 минут, увеличиваем для больших данных
      let timeout = 10 * 60 * 1000; // 10 минут по умолчанию
      if (data && typeof data === 'object') {
        // Если это FormData, пытаемся оценить размер
        if (data instanceof FormData) {
          // Для FormData увеличиваем таймаут до 15 минут
          timeout = 15 * 60 * 1000;
        } else if (data.size) {
          // Если есть размер, рассчитываем таймаут (примерно 1MB в секунду)
          const estimatedTime = (data.size / (1024 * 1024)) * 1000; // миллисекунды
          timeout = Math.max(timeout, estimatedTime * 2); // умножаем на 2 для запаса
          timeout = Math.min(timeout, 30 * 60 * 1000); // максимум 30 минут
        }
      }
      
      const axiosConfig = {
        method,
        url,
        timeout,
        headers,
      };
      
      if (data) {
        axiosConfig.data = data;
      }
      
      // Добавляем onUploadProgress если это загрузка файла
      if (options.onUploadProgress && (data instanceof FormData || options.isUpload)) {
        axiosConfig.onUploadProgress = options.onUploadProgress;
      }
      
      const response = await axios(axiosConfig);
      return {
        data: response.data,
        status: response.status,
        ok: response.status >= 200 && response.status < 300,
      };
    }
  } catch (error) {
    console.error('[HTTP] Ошибка запроса:', error);
    
    // Обработка ошибок
    if (error.response) {
      return {
        data: error.response.data,
        status: error.response.status,
        ok: false,
        error: formatErrorMessage(
          error.response.data?.error || error.response.data?.message || error.message,
          'Ошибка запроса'
        ),
      };
    }
    
    // Сетевая ошибка
    return {
      data: null,
      status: 0,
      ok: false,
      error: formatErrorMessage(error.message, 'Ошибка сети'),
      networkError: true,
    };
  }
}

/**
 * Shorthand методы
 */
export const http = {
  get: (endpoint, options) => httpRequest('GET', endpoint, null, options),
  post: (endpoint, data, options) => httpRequest('POST', endpoint, data, options),
  put: (endpoint, data, options) => httpRequest('PUT', endpoint, data, options),
  delete: (endpoint, options) => httpRequest('DELETE', endpoint, null, options),
};

export default http;

